import api from "@/services/CmsService";

// Country to locale mapping (populated dynamically at runtime)
export const COUNTRY_LOCALE_MAP: { [key: string]: string } = {};

// Known country codes (populated dynamically at runtime)
export const KNOWN_COUNTRIES: string[] = [];

// Global-only pages that don't have country-specific versions
export const GLOBAL_ONLY_PAGES = [
  "about-us",
  "contact-us-for-admissions-and-other-queries-regenesys",
  "privacy-policy",
  "terms-conditions",
  "alumni",
  "faculty",
  "leadership-team-and-faculty",
  "policy",
  "success-stories",
  "community-engagement",
  "school-funding",
  "accreditation",
  "accreditations-and-memberships",
  "testimonials",
  "all-academic-programmes",
  "student-loans",
  "careers",
  "vacancies",
];

// Country code to URL mapping for geolocation redirection (optional, empty by default)
export const COUNTRY_CODE_TO_URL_MAP: { [key: string]: string } = {};

// Country to locale mapping for backend validation (mirrors COUNTRY_LOCALE_MAP)
export const COUNTRY_TO_LOCALE_MAP: { [key: string]: string } = {};

// Simple cache for locales loading
let localesLastLoadedAt = 0;
const LOCALES_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
let localesLoadInFlight: Promise<void> | null = null;

// Slugify a locale name from CMS (e.g., "India" -> "india")
const slugify = (input: string): string =>
  input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-");

// Fetch locales from CMS and update constants in place
export const refreshLocalesFromCMS = async (): Promise<void> => {
  try {
    const response = await api.get("/api/i18n/locales");
    const locales: Array<{ name?: string; code: string; isDefault?: boolean }> =
      response?.data || [];
    // eslint-disable-next-line no-console

    const nextKnownCountries: string[] = [];
    const nextCountryLocaleMap: Record<string, string> = {};
    const nextCountryCodeUrlMap: Record<string, string> = {
      ZA: "south-africa",
    };

    for (const loc of locales) {
      const code = (loc?.code || "").trim();
      if (!code) continue;
      if (loc?.isDefault || code.toLowerCase() === "en") {
        nextCountryLocaleMap["south-africa"] = "en";
        if (!nextKnownCountries.includes("south-africa"))
          nextKnownCountries.push("south-africa");
        nextCountryCodeUrlMap["ZA"] = "south-africa";
        continue;
      }
      const name = (loc?.name || "").trim();
      if (!name) continue;
      const slug = slugify(name);
      if (!slug) continue;
      nextCountryLocaleMap[slug] = code;
      if (!nextKnownCountries.includes(slug)) nextKnownCountries.push(slug);
      const iso = code.includes("-") ? code.split("-")[1].toUpperCase() : "";
      if (iso) {
        nextCountryCodeUrlMap[iso] = slug;
      }
    }

    // Ensure stable order: south-africa first, then others alphabetically
    const orderedCountries = Array.from(new Set(nextKnownCountries)).sort(
      (a, b) => {
        if (a === "south-africa") return -1;
        if (b === "south-africa") return 1;
        return a.localeCompare(b);
      }
    );

    // Mutate KNOWN_COUNTRIES in place to preserve references
    KNOWN_COUNTRIES.splice(0, KNOWN_COUNTRIES.length, ...orderedCountries);

    // Mutate maps in place
    const keysToDelete = new Set(Object.keys(COUNTRY_LOCALE_MAP));
    for (const [country, locale] of Object.entries(nextCountryLocaleMap)) {
      COUNTRY_LOCALE_MAP[country] = locale;
      COUNTRY_TO_LOCALE_MAP[country] = locale;
      keysToDelete.delete(country);
    }
    // Remove stale keys
    for (const staleKey of keysToDelete) {
      if (staleKey !== "south-africa") {
        delete COUNTRY_LOCALE_MAP[staleKey];
        delete COUNTRY_TO_LOCALE_MAP[staleKey];
      }
    }

    // Replace COUNTRY_CODE_TO_URL_MAP contents
    const staleCodes = new Set(Object.keys(COUNTRY_CODE_TO_URL_MAP));
    for (const [code, slug] of Object.entries(nextCountryCodeUrlMap)) {
      COUNTRY_CODE_TO_URL_MAP[code] = slug;
      staleCodes.delete(code);
    }
    for (const stale of staleCodes) {
      delete COUNTRY_CODE_TO_URL_MAP[stale];
    }

    localesLastLoadedAt = Date.now();
  } catch {
    // Silently keep defaults on failure
    console.warn(
      "countryConstants: Failed to refresh locales from CMS, using defaults"
    );
  }
};

export const ensureLocalesLoaded = async (): Promise<void> => {
  const now = Date.now();
  if (now - localesLastLoadedAt < LOCALES_CACHE_TTL_MS) return;
  if (localesLoadInFlight) return localesLoadInFlight;
  localesLoadInFlight = refreshLocalesFromCMS().finally(() => {
    localesLoadInFlight = null;
  });
  return localesLoadInFlight;
};

// Utility function to get locale from country
export const getLocaleFromCountry = (country: string): string => {
  return COUNTRY_LOCALE_MAP[country] || "en";
};

// Common country codes that should be recognized even without locale mapping
export const COMMON_COUNTRIES = ["india", "kenya", "nigeria", "south-africa"];

// Utility function to detect country from URL path
export const detectCountryFromPath = (path: string): string | null => {
  const pathSegments = path.split("/")?.filter(Boolean);
  const firstSegment = pathSegments.length > 0 ? pathSegments[0] : null;
  const hasCountryPrefix =
    firstSegment &&
    (KNOWN_COUNTRIES.includes(firstSegment) || COMMON_COUNTRIES.includes(firstSegment));
  return hasCountryPrefix ? firstSegment : null;
};

// Utility function to get locale from URL path
export const getLocaleFromPath = (path: string): string => {
  const country = detectCountryFromPath(path);
  return country ? getLocaleFromCountry(country) : "en";
};

// Utility function to remove country prefix from path
export const removeCountryPrefix = (path: string): string => {
  const pathSegments = path?.split("/")?.filter(Boolean);
  const firstSegment = pathSegments?.length > 0 ? pathSegments[0] : null;
  const hasCountryPrefix =
    firstSegment &&
    (KNOWN_COUNTRIES.includes(firstSegment) || COMMON_COUNTRIES.includes(firstSegment));

  if (hasCountryPrefix) {
    const remainingPath = pathSegments?.slice(1).join("/") || "";
    return "/" + remainingPath;
  }

  return path;
};
